import { Button, Flex, Link, Spinner, Table, Text } from '@radix-ui/themes';
import mixpanel from 'mixpanel-browser';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import ErrorMessage from '~/components/ErrorMessage';
import Icon from '~/components/Icon';
import Loader from '~/components/Loader';
import LoadingOverlay from '~/components/LoadingOverlay';
import Pagination from '~/components/Pagination';
import SearchBar from '~/components/SearchBar';
import { TableRoot, TableRowWithOverflowShadows } from '~/components/Table';
import { overflowShadowShape } from '~/components/Table/use-overflow-shadows';
import SearchFilters from '~/containers/SearchFilters';
import { filterType } from '~/containers/SearchFilters/types';
import useAuthentication from '~/hooks/use-authentication';
import useNotification from '~/hooks/use-notification';
import { paginationType } from '~/hooks/use-pagination';
import { httpErrorMessagesFetcher } from '~/lib/errors';
import { capitalize } from '~/lib/strings';
import ColumnsControl from './ColumnsControl';
import HeaderCell from './HeaderCell';
import ModelDialog from './ModelDialog';
import TableRow from './TableRow';
import { actionsType, extrasDefinitionType, formDefinitionType, orderType } from './types';
import { columnsControlShape } from './use-columns-control';

const createLinkFnPropType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.arrayOf(PropTypes.func),
  PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      fn: PropTypes.func,
    }),
  ),
]);

const NoItemIconFlex = styled(Flex)`
  border-radius: var(--radius-5, 12px);
  border: 1px solid var(--neutral-4);
`;

function RenderNewButton({ label, onClick }) {
  return (
    <Button
      onClick={() => !!onClick && onClick()}
      color='gray'
      variant='outline'
      radius='full'
      highContrast
    >
      <Icon
        name='plus'
        width={16}
        height={16}
        inline
      />
      New {label}
    </Button>
  );
}

RenderNewButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

function NewButton({ noun, onClick, createLinkFn }) {
  const { url } = useRouteMatch();

  if (!createLinkFn) {
    return (
      <RenderNewButton
        label={noun}
        onClick={onClick}
      />
    );
  }

  if (typeof createLinkFn === 'object') {
    const to = createLinkFn.fn(url);
    return (
      <NavLink to={to}>
        <RenderNewButton label={createLinkFn.label} />
      </NavLink>
    );
  }

  return (
    <NavLink to={createLinkFn(url)}>
      <RenderNewButton label={noun} />
    </NavLink>
  );
}

NewButton.propTypes = {
  noun: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  createLinkFn: createLinkFnPropType,
};

function normalizeToFunctionArray(fn) {
  return [].concat(fn || []);
}

function NewButtons({ noun, onClick, createLinkFn, openCreateDialog }) {
  if (!onClick && !createLinkFn) {
    return (
      <RenderNewButton
        label={noun}
        onClick={openCreateDialog}
      />
    );
  }

  const formattedCreateLinkFn = normalizeToFunctionArray(createLinkFn);
  const formattedOnClick = normalizeToFunctionArray(onClick);

  const buttons = formattedCreateLinkFn.map((createLinkFn, index) => (
    <NewButton
      key={`createLinkFn${index}`}
      noun={noun}
      createLinkFn={createLinkFn}
    />
  ));

  formattedOnClick.forEach((onClick, index) => {
    buttons.push(
      <NewButton
        key={`onClick${index}`}
        noun={noun}
        onClick={onClick}
      />,
    );
  });

  return <Flex gap='4'>{buttons}</Flex>;
}

NewButtons.propTypes = {
  noun: PropTypes.string.isRequired,
  onClick: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
  createLinkFn: createLinkFnPropType,
  openCreateDialog: PropTypes.func,
};

function Model({
  noun,
  model,
  order,
  actions,
  goToFn,
  goToLinkFn,
  editLinkFn,
  createFn,
  createLinkFn,
  formDefinition,
  editFormDefinition,
  extrasDefinition,
  canCreate,
  canWrite,
  bulkAction,
  confirmEdit,
  ConfirmDialogContent,
  openDialogOnStart = false,
  links,
  fallbackEditToView = false,
  hideEditButton = false,
  hideTableTitle,
  showLimitSelector = false,
  customHeaderAction,
  errorMessages,
  columnsControl,
  overflowShadows,
  metaData,
  columnsToFiltersMapping,
  noItemIconName,
}) {
  const { isGod, isDemiGod } = useAuthentication();
  const { addNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState();
  const [editId, setEditId] = useState(null);
  const [confirmEditLoading, setConfirmEditLoading] = useState(false);
  const [confirmEditData, setConfirmEditData] = useState(null);
  const [haveConfirmed, setHaveConfirmed] = useState(false);
  const [shouldOpenDialogOnStart, setShouldOpenDialogOnStart] = useState(openDialogOnStart);

  const columnsToRender = columnsControl?.renderInTableOrder || order.filter(item => !item.hide);
  const { BulkActionDialog, BulkActionHeaderRow } = bulkAction || {};

  const isGodly = isGod || isDemiGod;

  function onDialogClose() {
    setEditId(null);
    setEditData(null);
    setConfirmEditData(null);
    setConfirmEditLoading(false);
    setHaveConfirmed(false);
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
  }

  function generateErrorMessage(error, method) {
    if (isGodly && error.response?.data?.message) return error.response?.data?.message;
    const errorMessage = httpErrorMessagesFetcher(
      errorMessages || {},
      error.response?.status,
      error.response?.data?.error,
    );
    return `Could not ${method} ${noun}: ${errorMessage || error.message}`;
  }

  const nounTitle = noun.replace(/^./, noun[0].toUpperCase());
  const hasRowButtons = actions?.length > 0 || canWrite || goToFn || goToLinkFn || columnsControl;

  const runConfirmEditTest = async payload => {
    try {
      await model.updateTest(payload);
      submitEdit(payload, true);
    } catch (error) {
      if (error.response?.status === 409) return setConfirmEditData(error.response.data);
      setConfirmEditLoading(false);
      addNotification({
        message: `Could not edit ${noun}: ${error.message}`,
        type: 'error',
      });
    }
  };

  const submitEdit = async (payload, haveConfirmed) => {
    const payloadWithId = { id: editId, ...payload };
    const shouldConfirm = confirmEdit && !haveConfirmed && model.updateTest;
    setLoading(true);
    if (shouldConfirm) return runConfirmEditTest(payloadWithId);

    setHaveConfirmed(true);
    try {
      await model.update(payloadWithId);
      setLoading(false);
      setIsEditDialogOpen(false);
      addNotification({
        message: 'Edit successful!',
        type: 'success',
      });
      setHaveConfirmed(false);
    } catch (error) {
      setLoading(false);
      addNotification({
        message: generateErrorMessage(error, 'edit'),
        type: 'error',
      });
    }
  };

  submitEdit.propTypes = {
    haveConfirmed: PropTypes.bool,
  };

  const confirmEditFn = async payload => {
    await submitEdit(payload, true);
  };

  const confirmEditDialogContent = ({ data }) => {
    return (
      <ConfirmDialogContent
        originalData={data}
        confirmEditFn={confirmEditFn}
        confirmEditData={confirmEditData}
        loading={confirmEditLoading}
        setConfirmEditData={setConfirmEditData}
        setIsEditDialogOpen={setIsEditDialogOpen}
        setLoading={setLoading}
      />
    );
  };

  async function submitCreate(data) {
    setLoading(true);
    try {
      await model.create(data);
      setIsCreateDialogOpen(false);
      setLoading(false);
      addNotification({
        message: `${nounTitle} created!`,
        type: 'success',
      });
    } catch (error) {
      setLoading(false);
      addNotification({
        message: generateErrorMessage(error, 'create'),
        type: 'error',
      });
    }
  }

  const listOfCanOrder = order.filter(o => o.canOrder);
  const startingStateOfSortableColumns = listOfCanOrder.map(item => ({ key: item.key, direction: null }));
  const location = useLocation();
  const queryString = new URLSearchParams(location.search);
  const sortParam = queryString.getAll('sort');

  const [sortableArray, setSortableArray] = useState(
    sortParam
      ? sortParam.map(item => {
          const [key, direction] = item.split(':');
          return { key, direction };
        })
      : startingStateOfSortableColumns,
  );

  function changeOrder(key, orderAcronym) {
    const newSortableArray = orderAcronym ? [{ key, direction: orderAcronym }] : [];
    setSortableArray(newSortableArray);
    if (model.order?.orderChange) model.order.orderChange(newSortableArray);
    mixpanel.track(`Sort ${noun}s table`, {
      'Table column sorted': key,
      'Table column sort direction': orderAcronym,
    });
  }

  useEffect(() => {
    if (!sortParam) setSortableArray([]);
  }, [sortParam]);

  async function startEdit(data) {
    const editForm = editFormDefinition || formDefinition;
    if (editLinkFn || !editForm) return;

    setEditData(data);
    setEditId(data.id || data.uuid);
    setIsEditDialogOpen(true);
  }

  useEffect(() => {
    if (openDialogOnStart && shouldOpenDialogOnStart) {
      setIsCreateDialogOpen(true);
      setShouldOpenDialogOnStart(false);
    }
  }, [openDialogOnStart, setIsCreateDialogOpen, shouldOpenDialogOnStart, setShouldOpenDialogOnStart]);

  const searchLoading = model?.search?.loading;
  const hasData = model?.data?.length > 0;
  const amountSelected = model?.selected?.length;
  const tableTitle = model?.pagination?.loadingTotalItems ? (
    <Flex
      gap='2'
      align='center'
    >
      {`${capitalize(noun)}s`}
      <Spinner />
    </Flex>
  ) : (
    `${capitalize(noun)}s${
      model.pagination ? ` (${Intl.NumberFormat('en-US').format(model.pagination.totalItems)})` : ''
    }`
  );

  const showLoading = !columnsToRender || searchLoading;
  const showNoData = !hasData;

  return (
    <Flex
      direction='column'
      gap='4'
    >
      <Flex
        direction='column'
        gap='5'
      >
        <Flex
          justify='between'
          width='100%'
        >
          <Flex
            align='center'
            gap='4'
          >
            {!hideTableTitle && (
              <Text
                size='6'
                weight='bold'
              >
                {tableTitle}
              </Text>
            )}
            {links?.map((item, index) => {
              return (
                <Button
                  asChild
                  key={index}
                  radius='full'
                  variant='ghost'
                >
                  <Link
                    underline='none'
                    href={item.url}
                    target='_blank'
                  >
                    {item.title}
                  </Link>
                </Button>
              );
            })}
          </Flex>
          <Flex
            gap='4'
            align='center'
          >
            {bulkAction && <BulkActionDialog sims={model} />}
            {canCreate && (
              <NewButtons
                noun={noun}
                onClick={createFn}
                createLinkFn={createLinkFn}
                openCreateDialog={() => setIsCreateDialogOpen(true)}
              />
            )}
          </Flex>
        </Flex>

        <Flex
          gap='2'
          justify='between'
        >
          <Flex
            gap='2'
            align='center'
            wrap='wrap'
          >
            {model.search && (
              <Flex
                direction='column'
                width={model.search.width || '300px'}
              >
                <SearchBar
                  placeholder={`Search for ${noun}s`}
                  {...model.search}
                />
              </Flex>
            )}
            {model.filters && (
              <SearchFilters
                model={model.filters}
                columnsControl={columnsControl}
                columnsToFiltersMapping={columnsToFiltersMapping}
              />
            )}
          </Flex>

          <Flex gap='2'>
            {customHeaderAction?.()}
            {model.pagination && model.pagination.totalPages > 0 && (
              <Pagination
                showLimitSelector={showLimitSelector}
                {...model.pagination}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      {showNoData && (
        <Flex
          height='320px'
          align='center'
          justify='center'
          direction='column'
          gap='4'
          position='relative'
        >
          {noItemIconName && (
            <NoItemIconFlex
              height='48px'
              width='48px'
              align='center'
              justify='center'
            >
              <Icon
                name={noItemIconName}
                width={24}
                height={24}
                color='var(--neutral-11)'
                inline
              />
            </NoItemIconFlex>
          )}
          <Text
            weight='regular'
            size='2'
          >
            No {noun}s found
          </Text>
          {showLoading && <LoadingOverlay />}
        </Flex>
      )}
      {!showNoData && (
        <TableRoot overflowShadows={overflowShadows}>
          {showLoading && <LoadingOverlay />}
          <Table.Header>
            <TableRowWithOverflowShadows
              columnsControl={columnsControl}
              overflowShadows={overflowShadows}
            >
              {columnsToRender.map(item => (
                <HeaderCell
                  key={item.key}
                  item={item}
                  sortableArray={sortableArray}
                  onChange={changeOrder}
                  data={model.data}
                  metaData={metaData}
                />
              ))}
              {hasRowButtons && (
                <Table.ColumnHeaderCell
                  align='right'
                  ref={overflowShadows ? overflowShadows.lastHeaderCellRef : undefined}
                >
                  {columnsControl && (
                    <ColumnsControl
                      columnsControl={columnsControl}
                      noun={noun}
                    />
                  )}
                </Table.ColumnHeaderCell>
              )}
            </TableRowWithOverflowShadows>
          </Table.Header>
          <Table.Body>
            {bulkAction && amountSelected > 0 && (
              <BulkActionHeaderRow
                amountSelected={amountSelected}
                count={model.bulkEdit.affectAll ? model.pagination?.totalItems : model.selected?.length}
                totalItems={model.pagination?.totalItems}
                setAffectAll={model.bulkEdit.setAffectAll}
                clearSelection={model.bulkEdit.clearSelection}
                affectAll={model.bulkEdit.affectAll}
                isGod={isGod}
                columnsControl={columnsControl}
                metaData={metaData}
              />
            )}
            {model.data.map((row, idx) => {
              return (
                <TableRow
                  key={idx}
                  data={row}
                  columnsToRender={columnsToRender}
                  canWrite={canWrite}
                  onClickEdit={startEdit}
                  editLinkFn={editLinkFn}
                  goToLinkFn={goToLinkFn}
                  goToFn={goToFn}
                  actions={actions}
                  fallbackEditToView={fallbackEditToView}
                  hideEditButton={hideEditButton}
                  hasRowButtons={hasRowButtons}
                  metaData={metaData}
                />
              );
            })}
          </Table.Body>
        </TableRoot>
      )}
      <Flex justify='between'>
        <Flex
          gap='4'
          align='center'
        >
          {bulkAction && !showNoData && (
            <Text color='gray'>
              {amountSelected} row{amountSelected !== 1 && 's'} selected
            </Text>
          )}
        </Flex>

        {model.pagination && model.pagination.totalPages > 0 && (
          <Pagination
            showLimitSelector={showLimitSelector}
            {...model.pagination}
          />
        )}
      </Flex>

      {formDefinition && isCreateDialogOpen && (
        <ModelDialog
          title={`Create ${noun}`}
          isOpen={isCreateDialogOpen}
          onDialogClose={onDialogClose}
          formDefinition={formDefinition}
          loading={loading}
          onClickSubmit={submitCreate}
        />
      )}

      {(editFormDefinition || formDefinition) && isEditDialogOpen && (
        <ModelDialog
          title={`Edit ${noun}`}
          isOpen={isEditDialogOpen}
          onDialogClose={onDialogClose}
          formDefinition={editFormDefinition || formDefinition}
          extrasDefinition={extrasDefinition}
          model={editData}
          loading={loading}
          onClickSubmit={submitEdit}
          showConfirm={confirmEditData && confirmEdit && !haveConfirmed}
          ConfirmDialogContent={confirmEditDialogContent}
        />
      )}
    </Flex>
  );
}

const modelPropsTypes = {
  isBoxedHeader: PropTypes.bool,
  isGodMode: PropTypes.bool,
  noun: PropTypes.string.isRequired,
  formDefinition: formDefinitionType,
  editFormDefinition: formDefinitionType,
  extrasDefinition: extrasDefinitionType,
  model: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    error: PropTypes.object,
    getDetails: PropTypes.func,
    create: PropTypes.func,
    update: PropTypes.func,
    updateTest: PropTypes.func,
    selected: PropTypes.array,
    setSelected: PropTypes.func,
    setAffectAll: PropTypes.func,
    affectAll: PropTypes.bool,
    bulkEdit: PropTypes.object,
    search: PropTypes.shape({
      value: PropTypes.string,
      onChange: PropTypes.func,
      loading: PropTypes.bool,
      placeholder: PropTypes.string,
      width: PropTypes.string,
    }),
    pagination: PropTypes.shape(paginationType),
    filters: filterType,
    order: PropTypes.shape({
      orderChange: PropTypes.func,
      order: PropTypes.string,
    }),
    columnsControl: columnsControlShape,
  }),
  canCreate: PropTypes.bool,
  canWrite: PropTypes.bool,
  order: orderType,
  actions: actionsType,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  goToFn: PropTypes.func,
  goToLinkFn: PropTypes.func,
  createFn: PropTypes.func,
  createLinkFn: createLinkFnPropType,
  editFn: PropTypes.func,
  editLinkFn: PropTypes.func,
  confirmEdit: PropTypes.func,
  bulkAction: PropTypes.shape({
    BulkActionDialog: PropTypes.elementType,
    BulkActionHeaderRow: PropTypes.elementType,
  }),
  ConfirmDialogContent: PropTypes.node,
  openDialogOnStart: PropTypes.bool,
  fallbackEditToView: PropTypes.bool,
  hideEditButton: PropTypes.bool,
  hideTableTitle: PropTypes.bool,
  showLimitSelector: PropTypes.bool,
  customHeaderAction: PropTypes.func,
  schema: PropTypes.object,
  errorMessages: PropTypes.object,
  columnsControlSettings: PropTypes.shape({
    initialColumns: PropTypes.arrayOf(PropTypes.string),
    hasColumnsControl: PropTypes.bool,
    stickyFirstColumn: PropTypes.bool,
    stickyLastColumn: PropTypes.bool,
  }),
  columnsControl: columnsControlShape,
  overflowShadows: overflowShadowShape,
  metaData: PropTypes.object,
  columnsToFiltersMapping: PropTypes.objectOf(PropTypes.string),
  noItemIconName: PropTypes.string,
};

Model.propTypes = modelPropsTypes;

function ModelPreRender(props) {
  const { model, noun, order } = props;

  if (!noun) throw new Error('Model component requires a noun prop');
  if (!order) throw new Error('Model component requires an order prop');
  if (!model) throw new Error('Model component requires a model prop');

  if (model.loading && !model?.search?.loading && !model.data) return <Loader />;
  if (model.error) return <ErrorMessage error={model.error} />;
  if (!model.data) return `No ${noun}s found`;

  return <Model {...props} />;
}

ModelPreRender.propTypes = modelPropsTypes;

export default ModelPreRender;
